<template>
  <div>
    <div class="loading-live-demo" v-if="loadingPlayDemo">
      <LoadingScreen />
    </div>
    <div
      class="live-demo-wrapper"
      :class="{'is-demo-mode': ahaGuestUser}"
      v-else
    >
      <template v-if="previewPresentationId">
        <div class="live-demo-header">
          <div
            v-if="!hideHeader"
            class=" preview-header"
          >
            <BackToEditorButton class="preview__back-btn" @exitPreviewMode="handleExitPreviewMode" />
            <span class="lg:text-base">
              {{ $i18nPresenter.t('This is a preview of your presentation') }} -
              <strong>“{{ presentationName }}”</strong>.
            </span>
            <div></div>
          </div>
          <div class="limit-slide-by-plan">
            <LimitSlideByPlan
              v-if="planBenefit.name === 'free' &&
                (planBenefitLimitation.poll || planBenefitLimitation.quiz)"
            />
          </div>
        </div>
      </template>

      <template v-else>
        <div
          class="flex px-4 pt-2 live-demo-header"
          v-if="!ahaGuestUser && !hideHeader"
        >
          <aha-logo
            width="140"
            height="32"
            class="block m-auto lg:ml-3.5 demo-logo"
          />
          <div class="flex-grow"></div>
          <div class="exit-button" @click="exit">
            <span class="mr-1">{{ $i18nPresenter.t('Exit demo') }}</span>
            <AhaIcon
              name="close"
              height="16"
              width="16"
              viewBox="0 0 24 24"
              class="cursor-pointer"
            />
          </div>
        </div>
      </template>

      <SignUpCTAHeader v-if="ahaGuestUser && !hideHeader" type="live-demo" />
      <div
        class="flex live-demo-content"
        id="live-demo-content"
        :class="{'top-limitation': planBenefit.name === 'free' &&
          (planBenefitLimitation.poll || planBenefitLimitation.quiz)}"
      >
        <div class="flex scale-box" id="scale-box">
          <div class="flex-grow demo-presentation">
            <div class="bg-presenter"></div>
            <div class="presenter-title">
              {{ $i18nPresenter.t('Presenter screen') }}
            </div>
            <div class="iframe-wrapper-presenter">
              <iframe
                :src="presenterDomain"
                id="iframe-presentation"
                v-if="presenterDomain"
              >
                <p>{{ $i18nPresenter.t('Your browser does not support iframes.') }}</p>
              </iframe>
            </div>
          </div>
          <div class="flex-none demo-audience">
            <div class="bg-audience"></div>
            <div class="presenter-title">
              {{ $i18nPresenter.t('Participant screen') }}
            </div>
            <div class="iframe-wrapper-audience">
              <iframe
                :src="audienceDomain"
                id="iframe-audience"
                v-if="presentation.id && audienceDomain"
              >
                <p>{{ $i18nPresenter.t('Your browser does not support iframes.') }}</p>
              </iframe>
            </div>
          </div>
        </div>
      </div>
      <div class="fixed-text">
        <p class="text-note" v-if="!previewPresentationId">
          <span v-if="!ahaGuestUser">
            {{ $i18nPresenter.t(`This presentation is live. You can interact with both screens to see how it works for both the presenter and the participants in real-time.`) }}
          </span>
          <span v-else>
            {{ $i18nPresenter.t(`This demo is interactive. Use both of the screens above to see how it works for the presenter and the participants in real-time.`) }}
          </span>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import Vue from 'vue'

import * as Sentry from '@sentry/vue'
import PresentationApi from '@/api/presentation'
import PresentationUtils from '@/utils/presentation'
import EventBus from '@/eventBus'
import config from '@/config'
import utils from '@/util'
import constants from '@/constant'
import LoadingScreen from '@/components-v2/common/loading/LoadingScreen.vue'
import UserApi from '@/api/user'
import cookie from '@/services/cookie'
import zoho from '@/services/zoho'
import SignUpCTAHeader from '@/components-v2/presentation-editor/demo-mode/SignUpCTAHeader'
import BackToEditorButton from '@/components-v2/presentation-editor/middle/BackToEditorButton.vue'
import LimitSlideByPlan from '@/components-v2/presentation-editor/middle/LimitSlideByPlan.vue'

export default {
  components: {
    LoadingScreen,
    SignUpCTAHeader,
    BackToEditorButton,
    LimitSlideByPlan,
  },
  props: {
    previewPresentationId: {
      type: Number,
      default: null,
    },
    previewPresentationUniqueAccessCode: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      presentation: {},
      isAlignItem: false,
      presenterDomain: '',
      audienceDomain: '',
      loadingPlayDemo: true,
      exiting: false,
      ahaToken: '',
      ahaGuestUser: null,
      hideHeader: false,
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.currentUser,
      slideActive: (state) => state.slideActive,
      presentationName: (state) => state.presentation.name,
      planBenefit: (state) => state.planBenefit,
      planBenefitLimitation: (state) => state.planBenefitLimitation,
    }),
  },
  created() {
    document.body.classList.add('overflow-hidden')
  },
  destroyed() {
    this.exit()
    EventBus.$off('window-resizeing')
    document.body.classList.remove('overflow-hidden')
    document.body.classList.remove('live-demo-body')
    window.removeEventListener('orientationchange', null)
    document.removeEventListener('keydown', this.onKeydown)
  },
  async mounted() {
    EventBus.$on('window-resizeing', () => {
      this.handleResizing()
    })
    this.ahaToken = cookie.getValue('ahaToken')
    this.ahaGuestUser = cookie.getValue('aha-guest-user')
    if (this.ahaToken) {
      await this.getCurrentUserAction()
    } else {
      await this.createGuestUser()
    }
    await this.openLiveDemo()
    document.addEventListener('keydown', this.onKeydown)
  },
  methods: {
    ...mapActions([
      'getCurrentUserAction',
      'setErrorOnInternalServerAction',
      'setIsServerConnectableAction',
      'updateSlideActiveAction',
    ]),
    async openLiveDemo() {
      await this.getLiveDemo()
      this.handleResizing()
      window.addEventListener('orientationchange', this.handleResizing())
    },

    handleResizing() {
      const elLiveDemo = document.getElementById('live-demo-content')
      const elScaleBox = document.getElementById('scale-box')
      if (!elLiveDemo || !elScaleBox) return
      const fixHeight = 649
      const fixWidth = 1680
      const height = window.innerHeight - 250
      const width = window.innerWidth

      const ratio = width / height
      let currentWidth
      let currentHeight
      if (ratio > fixWidth / fixHeight) {
        currentWidth = (height * fixWidth) / fixHeight
        currentHeight = height
      } else {
        currentHeight = (width * fixHeight) / fixWidth
        currentWidth = width
      }

      if (currentWidth > fixWidth) {
        currentWidth = fixWidth
        currentHeight = fixHeight
      }

      const scale = currentHeight / fixHeight
      elLiveDemo.style.width = `${currentWidth}px`
      elLiveDemo.style.height = `${currentHeight}px`
      if (height > width) {
        this.isAlignItem = true
      } else {
        this.isAlignItem = false
      }

      elScaleBox.style.transform = `scale(${scale})`
    },

    handleExitPreviewMode() {
      utils.mixpanelTracking(constants.eventCategories.PRESENTATION_EDITOR, 'Presentation Preview > Back to Editor > Close')
      this.exit()
    },

    exit() {
      if (this.exiting) return
      const prezHasResults = this.presentation && this.presentation.slides &&
        PresentationUtils.checkPresentationHasResults(this.presentation.slides)
      const payload = {
        uniqueAccessCode: this.presentation.uniqueAccessCode,
        presenting: false,
      }
      this.exiting = true

      if (this.presentation.id) {
        this.presenterDomain = ''
        if (!this.presentation.hasResults && prezHasResults) {
          payload.hasResults = true
        }
        PresentationApi.updatePresentationByPresentationId(this.presentation.id, payload)
      }

      setTimeout(this.redirectToPresentationList, 500)
    },
    redirectToPresentationList() {
      if (this.previewPresentationId) {
        this.$emit('close')
        return
      }
      window.location.href = `${this.presenterDomain}/apps/presentations`
    },
    async getLiveDemo() {
      const { id: presentationId } = this.$route.query
      const audienceProtocol = config.env === 'local' ? 'http' : 'https'

      this.loadingPlayDemo = true

      try {
        await this.setPresentation(presentationId)
        this.setDomains(audienceProtocol)
        this.loadingPlayDemo = false

        this.hideHeader = utils.getParamValue('header')
        if (this.hideHeader) {
          document.body.classList.add('live-demo-body')
          return
        }

        // only turn on chat for demo page without header
        // assuming here that hiding header = should not show chat
        // example: demo page embed in wordpress /features page has no header -> don't show chat
        zoho.init()
      } catch (err) {
        Sentry.captureException(err)
        if (err && err.status) {
          window.location.href = `${this.presenterDomain}/apps/presentations`
        } else {
          this.setErrorOnInternalServerAction(null)
        }
      }
    },

    async setPresentation(presentationId) {
      if (this.previewPresentationId && this.previewPresentationUniqueAccessCode) {
        this.presentation = {
          id: this.previewPresentationId,
          uniqueAccessCode: this.previewPresentationUniqueAccessCode,
        }
      } else {
        const params = presentationId ? { presentationId } : null
        this.presentation = await PresentationApi.getPresentationPlayDemo(this.currentUser.id, params)
      }
    },

    setDomains(audienceProtocol) {
      const { uniqueAccessCode } = this.presentation
      const whiteLabelLink = this.currentUser && this.currentUser.whiteLabelLink
      const whiteLabelPresenterDomain = this.currentUser && this.currentUser.whiteLabelPresenterDomain

      this.audienceDomain = whiteLabelLink
        ? `${audienceProtocol}://${whiteLabelLink}/${uniqueAccessCode}?fromIframe=true`
        : `${config.audienceDomain}/${uniqueAccessCode}?fromIframe=true`

      const prefixDomain = whiteLabelPresenterDomain
        ? `https://${whiteLabelPresenterDomain}`
        : config.presenterDomain

      this.presenterDomain = `${prefixDomain}/presentation/${this.presentation.id}?presenting=true&embedded=true`
    },

    async createGuestUser() {
      try {
        const res = await UserApi.createGuest({
          isGuest: true,
        })
        utils.setCookieToken(res.token)
        this.$store.dispatch('setCurrentUserAction', res.user)
        cookie.setValue('aha-guest-user', res.user && res.user.id)
        Vue.http.headers.common.Authorization = `Bearer ${res.token}`
        utils.mixpanelTracking(
          'Live Demo',
          'Live Demo > Create guest presenter > Succeeded',
        )
        this.ahaGuestUser = res.user.id
      } catch (error) {
        const errorMsg = error.body && error.body.message ? error.body.message : 'Network error'
        utils.mixpanelTracking(
          'Live Demo',
          'Live Demo > Create guest presenter > Failed',
          errorMsg,
        )

        throw error
      }
    },

    onKeydown(e) {
      if (config.EXIT_KEYS.includes(e.code)) {
        utils.mixpanelTracking(constants.eventCategories.PRESENTATION_EDITOR, 'Presentation Preview > Press Escape > Close')
        this.exit()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.live-demo-content {
  margin: 0 auto;
  .scale-box {
    transform-origin: top left;
    height: 649px;
    width: 1680px;
    margin: 0 auto;
  }

  &.top-limitation {
    margin-top: 142px;
  }
}

.demo-presentation {
  position: relative;
  width: 1280px;
  height: 700px;
}

#iframe-presentation {
  width: 100%;
  height: 100%;
  position: relative;
  z-index: 10;
}

.demo-audience {
  height: 654px;
  width: 375px;
  position: relative;
  background-color: #fff;
  border-radius: 10px;
  left: -36px;
}

#iframe-audience {
  position: relative;
  z-index: 10;
  width: 100%;
  height: 100%;
  border: 1px solid #D3D7E1;
}

.live-demo-header {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 10;
}

.exit-button {
  @apply p-4 text-sm cursor-pointer flex items-center text-base-80;
  @apply hover:text-primary-90;
}

.bg-presenter {
  background-image: url("../../assets/images/background/live-demo-presenter.svg");
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
}

.bg-audience {
  background-image: url("../../assets/images/background/live-demo-audience.svg");
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
}

.iframe-wrapper-presenter {
  width: 1104px;
  height: calc(1104px / 16 * 9);
  margin: 0 auto;
  margin-top: 16px;
}

.iframe-wrapper-audience {
  width: 336px;
  height: 536px;
  margin: 0 auto;
  margin-top: 50px;
}

.live-demo-wrapper {
  @apply absolute h-full w-full flex bg-base-0 items-center;
  &.is-demo-mode {
    .preview-header {
      @apply top-7 pb-1.5;
    }
    .live-demo-content {
      @apply pt-6;
    }
  }
  .preview-header {
    @apply flex items-center justify-between py-3 pl-6 border-b border-opacity-10 border-base-100;
    &::v-deep .escape-presenting {
      position: unset;
      opacity: 1;
    }
  }
}

.presenter-title {
  text-align: center;
  position: absolute;
  top: -40px;
  width: 100%;
  font-size: 20px;
  left: 0;
  color: #404658;
}

@media screen and (max-width:900px) and (orientation:landscape) {
  .live-demo-header {
    .demo-logo {
      margin: 0;
      width: 100px;
    }
  }
}

.toaster-live-demo {
  position: fixed;
  bottom: 0;
  left: 0;
  padding: 16px;
  z-index: 20;

  .toaster-content {
    padding: 16px;
    position: relative;
    box-shadow: 0px 4px 17px rgba(85, 85, 85, 0.16);
    border-radius: 12px;
    background-color: #fff;
  }
  .toaster-box {
    max-width: 480px;
    width: 100%;
    font-size: 14px;
    font-weight: normal;
    position: relative;
    padding-left: 20px;

    .border-right {
      position: absolute;
      left: 0;
      bottom: 0;
      width: 6px;
      height: 100%;
      background-color: #6A1EBB;
      border-radius: 12px;
    }

    a {
      font-weight: bold;
      font-size: 14px;
      display: block;
      margin-top: 8px;
      cursor: pointer;
      @apply text-primary-90;

      &:hover {
        @apply text-primary-100;
      }
    }
  }
}

.fixed-text {
  position: fixed;
  bottom: 0;
  left: 0;
  text-align: center;
  padding: 8px 16px;
  font-size: 14px;
  width: 100%;
  opacity: 0.8;
  p {
    max-width: 568px;
    margin: 0 auto;
    line-height: 1.3;
  }
}

.preview__back-btn {
  transition: opacity 0.15s;
  &::before {
    border-width: 0;
    border-left-color: transparent;
    border-right-color: transparent;
    border-top-color: transparent;
    top: 3.75rem;
    left: 2rem;
    margin-top: 0;
    margin-bottom: 0;
    border-style: solid;
    position: absolute;
    margin: 5px;
    z-index: 1;
    opacity: 0;
    content: '';
  }
  &::after {
    font-size: 0.8rem;
    line-height: 1rem;
    border-radius: 4px;
    padding: 5px 10px 4px;
    background-color: rgba(40, 46, 62);
    opacity: 0;
    position: absolute;
    top: 4.3rem;
    z-index: 2;
    color: #ffffff;
  }
}

@media (max-width: 1024px) {
  .fixed-text {
    font-size: 12px;

    p {
      max-width: 100%;
    }
  }
}

.limit-slide-by-plan {
  width: 100%;
  text-align: center;
  margin-top: 16px;
  &::v-deep .wrapper-box-limit {
    display: inline-block;
    margin: 0 auto;
  }
}
</style>
